/* eslint-disable max-len */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormArray,
  FormControl,
  UntypedFormControl,
  UntypedFormArray,
  AbstractControl,
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatStepper } from '@angular/material/stepper';
import {
  MatLegacySelect as MatSelect,
  MatLegacySelectChange as MatSelectChange,
} from '@angular/material/legacy-select';
import {
  MatLegacyDialogRef as MatDialogRef,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialog as MatDialog,
} from '@angular/material/legacy-dialog';

import { of, Subscription, Observable } from 'rxjs';
import { catchError, filter, finalize } from 'rxjs/operators';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { TsSnackbarService } from 'tsui';
import { Dictionary } from 'app/shared/models';
import { RelatedToType } from 'app/shared/enums';
import { ImportsService } from './../../imports.service';
import { CustomObject } from 'app/shared/modules/custom-objects/custom-objects.types';
import { ObjectsManagerService } from 'app/modules/settings/objects-manager/services/objects-manager.service';
import {
  OutputImportDialogData,
  ImportField,
  ImportResponse,
  InputImportDialogData,
  DocumentDetails,
} from '../../imports.types';
import { CustomFieldDialogComponent } from 'app/modules/settings/modals/custom-field-dialog/custom-field-dialog.component';
import { FieldsDialogData } from 'app/modules/settings/modals/custom-field-dialog/custom-field-dialog.types';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { FormatSnakeCasePipeUtility } from 'app/shared/pipes/format-snake-case.pipe';
import { AuthService } from '@core/services';
import { Permission } from '@core/enums';

@Component({
  selector: 'import-dialog',
  templateUrl: './import-dialog.component.html',
  styleUrls: ['./import-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImportDialogComponent extends OnDestroyMixin implements OnInit {
  private fieldsList: ImportField[];
  private fieldsMappingControl: UntypedFormArray = new UntypedFormArray([]);

  importEnableObjects: CustomObject[];
  stepOneForm: FormGroup;
  stepTwoForm: FormGroup;
  isStepLoading: boolean;
  acceptedFileExtensions = ['.xlsx, .csv'];
  importRecord: ImportResponse;
  filteredFieldsList: ImportField[];
  importEnableObjectsCtrl: UntypedFormControl = new UntypedFormControl();
  filteredImportEnableObjects: CustomObject[];
  importFieldsError$: Observable<any>;
  updateImportError$: Observable<any>;
  firstStepValidationErrors: string[];
  modeOptions = ['create', 'update', 'create_and_update'];
  idMappingErrors: string[];

  @ViewChild('importStepper') private _stepper!: MatStepper;
  @ViewChildren('selectElement') private _selectElements: QueryList<MatSelect>;

  get mapping(): FormArray<AbstractControl> {
    return this.stepTwoForm.controls['mapping'] as FormArray;
  }

  get autoMapping(): Array<{ header: string; field: ImportField }> {
    return this.importRecord?.auto_mapping || this.dialogData?.import.auto_mapping;
  }

  get mappedValues(): Dictionary<ImportField> {
    return this.importRecord?.mapping || this.dialogData?.import.mapping;
  }

  get dataPreview(): Dictionary<string[]> {
    return this.importRecord?.data_preview || this.dialogData?.import.data_preview;
  }

  get isEditMode(): boolean {
    return this.dialogData.editMode;
  }

  get stepperIndex(): number {
    return this._stepper?.selectedIndex || 0;
  }

  get importModeFieldPlaceholder(): string | null {
    return this.isEditMode && this.dialogData.import.mode
      ? FormatSnakeCasePipeUtility.transform(this.dialogData.import.mode)
      : null;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: InputImportDialogData,
    private readonly _matDialog: MatDialog,
    private readonly _matDialogRef: MatDialogRef<ImportDialogComponent>,
    private readonly _formBuilder: FormBuilder,
    private readonly _cdRef: ChangeDetectorRef,
    private readonly _importsService: ImportsService,
    private readonly _objectsManagerService: ObjectsManagerService,
    private readonly _confirmService: ConfirmService,
    private readonly _authService: AuthService,
    private readonly snackBar: TsSnackbarService,
  ) {
    super();
    this._objectsManagerService.bustObjectsCache();
  }

  ngOnInit(): void {
    this.buildStepperForms();
    this.getObjects$();
    this.watchObjectsOptionsSearchChanges();
  }

  public getImportFields$(objectType: RelatedToType): Subscription {
    return this._importsService
      .getFields$(this.dialogData.accountId, objectType)
      .pipe(
        untilComponentDestroyed(this),
        catchError((error) => (this.importFieldsError$ = of(error))),
        finalize(() => this._cdRef.detectChanges()),
      )
      .subscribe((fields: ImportField[]) => {
        this.fieldsList = fields;
        this.filteredFieldsList = this.fieldsList;
      });
  }

  /**
   * Fetch custom objects, filter objects if import value equals to true.
   * @returns an array of custom objects, assign to a variable to be iterated inside a template.
   */
  getObjects$(): Subscription {
    if (!this.isEditMode)
      return this._objectsManagerService
        .get(this.dialogData.accountId)
        .pipe(untilComponentDestroyed(this))
        .subscribe((objects) => {
          this.importEnableObjects = objects.filter((object) => object.import === true);
          this.filteredImportEnableObjects = this.importEnableObjects;
          this._cdRef.detectChanges();
        });

    return;
  }

  buildStepperForms(): void {
    this.stepOneForm = this._formBuilder.group({
      object_type: [
        {
          value: this.isEditMode ? this.dialogData.import.related_to_type : null,
          disabled: this.isEditMode,
        },
        [Validators.required],
      ],
      uploaded_file: [this.isEditMode ? this.dialogData.import?.document : null, [Validators.required]],
      mode: [this.isEditMode ? this.dialogData.import.mode : null, [Validators.required]],
    });
    this.stepTwoForm = this._formBuilder.group({
      mapping: new FormArray([], Validators.required),
    });
  }

  // Make a GET request to fields API based on user object type selection.
  public onObjectTypeChange(event: MatSelectChange): void {
    this.getImportFields$(event.value);
  }

  public fetchFormValues(formGroup: FormGroup, formControl: string) {
    return formGroup.get(formControl)?.value === 'Project' ? 'Deal' : formGroup.get(formControl)?.value;
  }

  public checkStepperFormErrors(form: FormGroup): boolean {
    if (this.isEditMode) return false;
    else return form.pristine || form.invalid;
  }

  // Check if all mapping step mat-select values are empty, preventing the user to advance to step 3.
  public isFieldsMappingEmpty(): boolean {
    // Commented out because the user can skip non-mandatory fields during the mapping process.
    // const allFilled = this._selectElements.toArray().every((select) => select.value !== null);
    // return allFilled && this.stepTwoForm.valid;
    return this.stepTwoForm.valid;
  }

  /**
   * Check if any TS field has the same option value selected, if so alert the user and prevent it from proceeding.
   * Convert the selected values to an array, map to its value and filter out null values (editMode scenario).
   * Use Set to check for unique values.
   * @returns true if all non-null values are unique (length of Set is the same as the number of non-null mat-select elements).
   */
  public isFieldsMappingDuplicated(): boolean {
    const selectedValues = this._selectElements
      .toArray()
      .map((select) => select.value)
      .filter((value) => value !== null);

    const uniqueValuesSet = new Set(selectedValues);
    return uniqueValuesSet.size === selectedValues.length;
  }

  watchObjectsOptionsSearchChanges(): void {
    this.importEnableObjectsCtrl.valueChanges
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => this.filterObjectsOptions());
  }

  private filterObjectsOptions(): void {
    if (!this.importEnableObjects) return;

    const filteredOptions = this.importEnableObjectsCtrl.value?.toLowerCase();
    this.filteredImportEnableObjects = this.importEnableObjects.filter(
      (option) => option.label.toLowerCase().indexOf(filteredOptions) > -1,
    );
  }

  private watchFieldsMappingSearchChanges(): void {
    this.fieldsMappingControl.controls.forEach((control: AbstractControl) =>
      control.valueChanges.pipe(untilComponentDestroyed(this)).subscribe(() => this.filterMappingSearchOptions()),
    );
  }

  private filterMappingSearchOptions(): void {
    if (!this.fieldsList) return;

    const filteredFields = this.fieldsMappingControl.value.map((controlValue: string) =>
      controlValue ? controlValue.toLowerCase() : '',
    );

    this.filteredFieldsList = this.fieldsList.filter((option) => {
      const label = option.label.toLowerCase();
      return filteredFields.some((value: string) => label.includes(value));
    });
  }

  private addDropDownValue(header: string, field: ImportField): void {
    const mappingGroup = this._formBuilder.group({
      header: new FormControl(header),
      field: new FormControl(field?.name ? field.name : field),
      filterValue: [''],
    });
    this.mapping.push(mappingGroup);
  }

  public onFirstStepNextClick(form: FormGroup): void {
    this.isStepLoading = true;
    // If user is editing an existing import...
    if (this.isEditMode) this.editImport(form);
    // ...else its a new import.
    else this.createImport(form);

    this.watchFieldsMappingSearchChanges();
    this._selectElements.changes.pipe(untilComponentDestroyed(this)).subscribe(() => this.isFieldsMappingEmpty());
  }

  public clearFieldSelection(index: number): void {
    this.mapping.controls[index].patchValue({ field: null });
  }

  /**
   * Open custom field dialog and populate related mat-select with recently created custom field.
   * @param index mapping control index position. Used to replace the old control value by the new custom field value.
   */
  public openCustomFieldDialog(index: number): void {
    this._matDialog
      .open(CustomFieldDialogComponent, {
        disableClose: true,
        data: { mode: 'add', fromImport: true, importObjectType: this.stepOneForm.get('object_type').value },
      })
      .afterClosed()
      .pipe(
        untilComponentDestroyed(this),
        filter((response) => !!response),
      )
      .subscribe((response: FieldsDialogData) => {
        this.getImportFields$(response.related_to_type);
        this.mapping.controls[index].patchValue({ field: response.name });
        this._cdRef.detectChanges();
      });
  }

  public onSecondStepNextClick(): void {
    this.isStepLoading = true;

    const columnMapping = {};

    const { value } = this.mapping;

    value.map((item: { header: string; field: ImportField }) =>
      item.field?.name !== null && item.field !== null
        ? (columnMapping[item.header] = item.field?.name ? item.field.name : item.field)
        : delete columnMapping[item.header],
    );

    this._importsService
      .update$(
        this.dialogData.accountId,
        this.isEditMode ? this.dialogData.import.id : this.importRecord.id,
        columnMapping,
        null,
      )
      .pipe(
        untilComponentDestroyed(this),
        finalize(() => {
          this.isStepLoading = false;
          this._cdRef.detectChanges();
        }),
      )
      .subscribe({
        next: () => {
          this._stepper.next();
          this.idMappingErrors = null;
        },
        error: (err: HttpErrorResponse) => {
          this.idMappingErrors = err.error.import.errors;
        },
      });
  }

  public onSecondStepBackClick(): void {
    // Here we have to delete the import because on first step next button click, we do a POST request to register the new import record.
    if (!this.isEditMode) {
      this._importsService
        .delete$(this.dialogData.accountId, this.importRecord.id)
        .pipe(untilComponentDestroyed(this))
        .subscribe();
    }
    // Clear mapping controls to avoid column duplicity and validation errors if user goes back to step 1.
    this.mapping.clear();
    this._stepper.previous();
    this.idMappingErrors = null;
  }

  public displayMappedFieldLabel(fieldName: string): string {
    const field = this.filteredFieldsList.find((item) => item.name === fieldName);
    return field ? field.label : 'Not mapped';
  }

  private isSameUploadedFile(
    uploadedFile: File | DocumentDetails | { value: DocumentDetails },
    fetchedFile: DocumentDetails,
  ): boolean {
    const uploadedFileValue = 'value' in uploadedFile ? uploadedFile.value : uploadedFile;

    if (uploadedFileValue instanceof File) {
      return (
        uploadedFileValue.name === fetchedFile.public_name &&
        uploadedFileValue.size === fetchedFile.attachment_file_size &&
        uploadedFileValue.type === fetchedFile.attachment_content_type &&
        new Date(uploadedFileValue.lastModified).getTime() === new Date(fetchedFile.created_at).getTime()
      );
    } else {
      return (
        uploadedFileValue.public_name === fetchedFile.public_name &&
        uploadedFileValue.attachment_file_size === fetchedFile.attachment_file_size &&
        uploadedFileValue.attachment_content_type === fetchedFile.attachment_content_type &&
        new Date(uploadedFileValue.created_at).getTime() === new Date(fetchedFile.created_at).getTime()
      );
    }
  }

  private editImport(form: FormGroup): void {
    const isSameFile = this.isSameUploadedFile(form.get('uploaded_file').value, this.dialogData.import?.document);

    // Fetch fields related to object type in here because the mat-select dropdownChange is disabled on edit mode.
    this.getImportFields$(
      this.dialogData.import.related_to_type === RelatedToType.Deal
        ? RelatedToType.Project
        : this.dialogData.import.related_to_type,
    );

    this._importsService
      .update$(this.dialogData.accountId, this.dialogData.import.id, null, form, isSameFile)
      .pipe(untilComponentDestroyed(this))
      .subscribe({
        next: (updatedImport) => {
          this.importRecord = updatedImport;
          if (this.importRecord.headers) {
            this.importRecord.headers.forEach((header, index) => {
              this.addDropDownValue(
                header,
                (this.autoMapping[index]?.field as ImportField) || this.mappedValues[header],
              );
            });
            this._stepper.next();
          }
        },
        error: (err) => {
          if (err.error?.import.errors) {
            this.firstStepValidationErrors = err.error.import.errors;
          } else {
            this.snackBar.open(
              'An error has occurred. You can fix the current file, upload a new one or choose a different mode option.',
              'error',
              null,
              {
                duration: 5000,
              },
            );
          }
          this.isStepLoading = false;
          this.stepOneForm.get('uploaded_file').setErrors({ invalid: true });
          this._cdRef.detectChanges();
        },
        complete: () => {
          this.isStepLoading = false;
        },
      });
  }

  private createImport(form: FormGroup): void {
    this._importsService
      .add$(this.dialogData.accountId, form)
      .pipe(untilComponentDestroyed(this))
      .subscribe({
        next: (newImport) => {
          this.importRecord = newImport;
          if (this.importRecord.headers) {
            this.importRecord.headers.forEach((header, index) => {
              this.addDropDownValue(header, this.autoMapping[index]?.field || this.mapping.controls[index]?.value);
            });
            this._stepper.next();
          }
        },
        error: (err) => {
          if (err.error?.import.errors) {
            this.firstStepValidationErrors = err.error.import.errors;
          } else {
            this.snackBar.open(
              'An error has occurred. You can fix the current file, upload a new one or choose a different mode option.',
              'error',
              null,
              {
                duration: 5000,
              },
            );
          }
          this.isStepLoading = false;
          this.stepOneForm.get('uploaded_file').setErrors({ invalid: true });
          this._cdRef.detectChanges();
        },
        complete: () => {
          this.isStepLoading = false;
        },
      });
  }

  /**
   * Reset the fileValidationErrors variable to null on file change and populate related formControl with the file data.
   */
  public onImportFileChange(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const file: File | null = inputElement.files[0];
    this.stepOneForm.get('uploaded_file').setValue(file);
    this.firstStepValidationErrors = null;
  }

  // Reset the firstStepValidationErrors variable to null on import mode change, set new value as element's placeholder and populate related formControl with the mode option data.
  public onModeChange(event: MatSelectChange): void {
    event.source.placeholder = FormatSnakeCasePipeUtility.transform(event.value);
    this.stepOneForm.get('mode').setValue({ value: event.value });
    this.firstStepValidationErrors = null;
  }

  public dismiss(data?: OutputImportDialogData): void {
    this._confirmService
      .openConfirmationModal({
        title: `Close confirmation`,
        message: `
          <p>Are you sure you want to close the import wizard?</p>
          ${this.stepperIndex > 0 ? '<p>You can resume your import later.</p>' : ''}
        `,
      })
      .pipe(
        untilComponentDestroyed(this),
        filter((result: boolean) => result),
      )
      .subscribe(() => this._matDialogRef.close(data));
  }

  // Send mapped fields & import ID values back to parent component.
  public saveAndClose(): void {
    this._matDialogRef.close({
      recordId: this.isEditMode ? this.dialogData.import.id : this.importRecord.id,
    });
  }
}
